import {
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Tag,
  useDisclosure,
} from '@chakra-ui/react';
import {
  createPrismaQuery,
  createPrismaSort,
  DataTable,
  DataTableAddFilter,
  DataTableFilter,
  DataTableFilters,
  DataTableSearch,
  Loosen,
  useCrud,
} from '@pelicargo/datatable';
import { CargoType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import {
  allCargoTypeOptions,
  freightServiceOptions,
  getPrettyCargoType,
  getPrettySurchargeCategory,
  prettyDate,
  serviceTypeOptions,
  surchargeCategoryOptions,
} from '@pelicargo/utils';
import { Provider, SurchargeCategory } from '@prisma/client';
import { createColumnHelper } from '@tanstack/react-table';
import { omit } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import { HiEllipsisVertical } from 'react-icons/hi2';

import { SurchargeDrawer } from '../components/Drawers/SurchargeDrawer';
import { useSurcharges } from '../hooks/surcharge/useSurcharges';
import { SurchargeActionState } from '../utils/types';

const now = new Date();

const surchargeFilters: DataTableFilter[] = [
  {
    name: 'status',
    label: 'Status',
    type: 'string',
    inputType: 'radio',
    operator: 'in',
    value: [],
    options: [
      { label: 'Active', value: 'active' },
      { label: 'Upcoming', value: 'upcoming' },
      { label: 'Replaced', value: 'replaced' },
      { label: 'Expired', value: 'expired' },
    ],
  },
  {
    name: 'cargo_type',
    label: 'Cargo Type',
    type: 'string',
    inputType: 'checkbox',
    operator: 'in',
    value: [],
    options: allCargoTypeOptions,
  },
  {
    name: 'surcharge_category',
    label: 'Category',
    type: 'string',
    inputType: 'checkbox',
    operator: 'in',
    value: [],
    options: surchargeCategoryOptions,
  },
  {
    name: 'service_type',
    label: 'Service Type',
    type: 'string',
    inputType: 'checkbox',
    operator: 'in',
    value: [],
    options: serviceTypeOptions,
  },
  {
    name: 'freight_service',
    label: 'Freight Service',
    type: 'string',
    inputType: 'checkbox',
    operator: 'in',
    value: [],
    options: freightServiceOptions,
  },
  {
    name: 'starts_at',
    label: 'Valid From',
    type: 'date',
    inputType: 'date',
    operator: 'gte',
    showOperator: true,
    value: new Date(),
  },
  {
    name: 'expires_at',
    label: 'Valid To',
    type: 'date',
    inputType: 'date',
    operator: 'lte',
    showOperator: true,
    value: new Date(),
  },
  {
    name: 'is_optional',
    label: 'Is Optional',
    type: 'boolean',

    inputType: 'radio',
    operator: 'equals',
    value: undefined,
    options: [
      {
        label: 'Optional',
        value: true,
      },
      {
        label: 'Not Optional',
        value: false,
      },
    ],
  },
  {
    name: 'provider_id',
    label: 'Provider ID',
    inputType: 'string',
    type: 'string',
    operator: 'contains',
    mode: 'insensitive',
    value: '',
  },
  // note that only mode and value are used to create the prisma query for surcharges
  {
    name: 'account.name',
    label: 'Account',
    inputType: 'string',
    type: 'string',
    operator: 'contains',
    mode: 'insensitive',
    value: '',
  },
];

export const createSurchargePrismaQuery = (filters: typeof surchargeFilters) => {
  // we handle the account filter differently, so we separate the filters
  const someFilters = filters?.filter((filter) => filter.label !== 'Account');
  const accountFilter = filters?.filter((filter) => filter.label === 'Account')?.[0];

  const query = createPrismaQuery(someFilters);

  if (accountFilter) {
    const { value, mode } = accountFilter;
    query['account'] = {} as any; // bleh
    query['account']['name'] = { contains: value, mode };
  }

  if (query?.status?.['in']?.includes('active')) {
    return {
      ...omit(query, 'status'),
      starts_at: { lte: now },
      expires_at: { gte: now },
    };
  }

  if (query?.status?.['in']?.includes('upcoming')) {
    return {
      ...omit(query, 'status'),
      starts_at: { gte: now },
      expires_at: { gte: now },
      replaced_by_id: { equals: null },
    };
  }

  if (query?.status?.['in']?.includes('replaced')) {
    return {
      ...omit(query, 'status'),
      replaced_by_id: { not: null },
    };
  }

  if (query?.status?.['in']?.includes('expired')) {
    return {
      ...omit(query, 'status'),
      expires_at: { lte: now },
    };
  }

  return omit(query, 'status');
};

type TableColumns = {
  id: number;
  tags?: string[];
  provider_id: string;
  airline_label: string;
  airline_code: string;
  airline_product: string;
  surcharge_category: SurchargeCategory;
  is_optional: boolean;
  cargo_type: string;
  service_type: string;
  min_cost: number;
  per_unit_cost: number;
  unit: string;
  starts_at: Date;
  expires_at: Date;
  comments: string;
  replaced_by_id: number;
  action?: any;
  account: { name: string };
  provider: Provider;
};

export const SurchargesScreen = () => {
  const surchargeDrawer = useDisclosure();

  const { query, handleQueryChange, filters, setFilters, sorting, setSorting, pagination, setPagination, take, skip } =
    useCrud<typeof surchargeFilters>();

  const [selectedSurchargeId, setSelectedSurchargeId] = useState<number | null>(null);
  const [state, setState] = useState<SurchargeActionState | null>(null);

  const { data, count, refetch } = useSurcharges({
    skip,
    take,
    search: query,
    filters: createSurchargePrismaQuery(filters),
    orderBy: createPrismaSort(sorting),
  });

  const onRowClick = useCallback(
    (action: SurchargeActionState, row: TableColumns) => {
      setState(action);
      setSelectedSurchargeId(row?.id);
      surchargeDrawer.onOpen();
    },
    [surchargeDrawer],
  );

  const handleCreateDrawer = () => {
    setSelectedSurchargeId(null);
    setState('create');
    surchargeDrawer.onOpen();
  };

  const handleAddFilter = (filter: Loosen<typeof surchargeFilters>[0]) => () => {
    setFilters((prevFilters) => [...prevFilters, filter]);
  };

  const handleClear = () => {
    setFilters([]);
  };

  const handleClose = () => {
    setState(null);
    setSelectedSurchargeId(null);
    refetch();
    surchargeDrawer.onClose();
  };

  const columnHelper = createColumnHelper<TableColumns>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: 'ID',
        size: 0,
        minSize: 0,
      }),
      columnHelper.accessor('tags', {
        header: 'Status',
        enableSorting: false,
        cell: (cell) => {
          const rowData = cell.row.original;
          const tags = [];
          const now = new Date();
          const expires_at = new Date(rowData.expires_at);
          const starts_at = new Date(rowData.starts_at);
          const replaced = !!rowData.replaced_by_id;
          const expired = rowData.expires_at && expires_at < now;

          // Rules to display tags
          // Without the `key` attribute, the Tag elements will have duplicate keys and react will whine.
          if (expired) {
            tags.push(
              <Tag colorScheme="red" key={`expired_${cell.row.id}`}>
                Expired
              </Tag>,
            );
          }
          if (starts_at < now && !expired) {
            tags.push(
              <Tag colorScheme="green" key={`active_${cell.row.id}`}>
                Active
              </Tag>,
            );
          }
          if (starts_at > now && !expired && !replaced) {
            tags.push(
              <Tag colorScheme="orange" key={`upcoming_${cell.row.id}`}>
                Upcoming
              </Tag>,
            );
          }
          if (replaced) {
            tags.push(
              <Tag colorScheme="blue" key={`replaced_${cell.row.id}`}>
                Replaced
              </Tag>,
            );
          }

          return <HStack>{tags}</HStack>;
        },
      }),
      columnHelper.accessor('provider_id', {
        header: 'Airline',
        size: 0,
        minSize: 0,
      }),
      columnHelper.accessor('provider', {
        header: 'Provider Name',
        size: 0,
        minSize: 0,
        cell: (cell) => cell.getValue().name,
      }),
      columnHelper.accessor('airline_label', {
        header: 'Airline Label',
      }),
      columnHelper.accessor('surcharge_category', {
        header: 'Surcharge Category',
        cell: (cell) => getPrettySurchargeCategory(cell.getValue()),
      }),
      columnHelper.accessor('cargo_type', {
        header: 'Cargo Type',
        cell: (cell) => getPrettyCargoType(cell.getValue() as CargoType),
      }),
      columnHelper.accessor('min_cost', {
        header: 'Minimum Cost',
      }),
      columnHelper.accessor('per_unit_cost', {
        header: 'Per Unit Cost',
      }),
      columnHelper.accessor('unit', {
        header: 'Unit Type',
      }),
      columnHelper.accessor('starts_at', {
        header: 'Valid From',
        cell: (cell) => prettyDate(cell.getValue() as any, { timeZone: 'Etc/UTC' }),
      }),
      columnHelper.accessor('expires_at', {
        header: 'Valid To',
        cell: (cell) => prettyDate(cell.getValue() as any, { timeZone: 'Etc/UTC' }),
      }),
      columnHelper.accessor('comments', {
        header: 'Comments',
      }),
      columnHelper.accessor('account', {
        header: 'Account',
        cell: (cell) => cell.getValue()?.name ?? '',
      }),
      columnHelper.accessor('action', {
        header: '',
        size: 0,
        minSize: 0,
        cell: (cell) => {
          const row = cell.row.original;

          return (
            <Box onClick={(e) => e.stopPropagation()}>
              <Menu size="xs">
                <MenuButton
                  as={IconButton}
                  aria-label="Options"
                  size="sm"
                  icon={<HiEllipsisVertical />}
                  variant="ghost"
                />
                <MenuList>
                  <MenuItem onClick={() => onRowClick('expire', row)}>Expire</MenuItem>
                  <MenuItem onClick={() => onRowClick('replace', row)}>Replace</MenuItem>
                </MenuList>
              </Menu>
            </Box>
          );
        },
      }),
    ],
    [columnHelper, onRowClick],
  );

  return (
    <>
      <Stack spacing="0" h="full">
        <Stack p="4" spacing="4">
          <Stack direction="row" justify="space-between" zIndex="99" w="full">
            <Stack direction="row" align="center" w="full">
              <Heading variant="h3">Surcharges</Heading>
              <DataTableSearch value={query} onChange={handleQueryChange} />
              <DataTableAddFilter
                activeFilters={filters?.map((filter) => filter.name)}
                options={surchargeFilters}
                handleAddFilter={handleAddFilter}
              />
            </Stack>
            <Button colorScheme="brand" onClick={handleCreateDrawer}>
              Create Surcharge
            </Button>
          </Stack>
          <Show if={filters.length > 0}>
            <DataTableFilters filters={filters} setFilters={setFilters} onClear={handleClear} />
          </Show>
        </Stack>
        <DataTable
          columns={columns}
          data={data ?? []}
          dataCount={count ?? 0}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          onRowClick={(row) => onRowClick('replace', row)}
        />
      </Stack>
      <Show if={surchargeDrawer.isOpen}>
        <SurchargeDrawer
          state={state}
          surchargeId={selectedSurchargeId}
          isOpen={surchargeDrawer.isOpen}
          onClose={handleClose}
        />
      </Show>
    </>
  );
};
